import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Send, Loader2, User, AlertCircle, RefreshCw } from 'lucide-react'
import { NicolAIAvatar } from '@/shared/components'
import { useScopingAIAssistant } from '../hooks'
import type { Project } from '@/shared/types'
import type { ScopingAIMessage } from '@/shared/types/database'
import { cn } from '@/shared/lib/utils'

interface ScopingAIPanelProps {
  project: Project
  onSpecsUpdate?: (update: { section: string; content: string }) => void
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function MessageBubble({ message }: { message: ScopingAIMessage }) {
  const isAssistant = message.role === 'assistant'

  return (
    <div
      className={cn(
        'flex gap-3',
        isAssistant ? 'justify-start' : 'justify-end'
      )}
    >
      {isAssistant && <NicolAIAvatar size="md" />}
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isAssistant
            ? 'rounded-tl-md bg-white text-foreground shadow-sm'
            : 'rounded-tr-md bg-accent text-white'
        )}
      >
        <div
          className={cn(
            'prose prose-sm max-w-none',
            isAssistant
              ? 'prose-slate'
              : 'prose-invert prose-p:text-white prose-strong:text-white prose-a:text-white/90'
          )}
        >
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
        <div
          className={cn(
            'mt-1 text-[10px]',
            isAssistant ? 'text-foreground-muted' : 'text-white/70'
          )}
        >
          {formatTime(message.created_at)}
        </div>
      </div>
      {!isAssistant && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200">
          <User className="h-4 w-4 text-slate-600" />
        </div>
      )}
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-3">
      <NicolAIAvatar size="md" />
      <div className="flex items-center gap-1 rounded-2xl rounded-tl-md bg-white px-4 py-3 shadow-sm">
        <span className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted [animation-delay:-0.3s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted [animation-delay:-0.15s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted" />
      </div>
    </div>
  )
}

export function ScopingAIPanel({ project, onSpecsUpdate }: ScopingAIPanelProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Build project context for the AI
  const projectContext = {
    name: project.name,
    description: project.description,
    objective: project.objective,
    targetUsers: project.target_users,
    existingAssets: project.existing_assets,
    constraints: project.constraints,
    inspirations: project.inspirations,
  }

  const {
    messages,
    isLoading,
    error,
    isRateLimited,
    requestCount,
    maxRequests,
    sendMessage,
    retry,
  } = useScopingAIAssistant(project.id, projectContext, project.specs_content, {
    onSpecsUpdate,
  })

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Auto-resize textarea (min 3 lines, max 6 lines)
  const MIN_HEIGHT = 76 // ~3 lines with padding
  const MAX_HEIGHT = 140 // ~6 lines with padding

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)
    // Reset height to calculate scrollHeight properly
    e.target.style.height = `${MIN_HEIGHT}px`
    // Set height to scrollHeight, clamped between min and max
    const newHeight = Math.max(MIN_HEIGHT, Math.min(e.target.scrollHeight, MAX_HEIGHT))
    e.target.style.height = `${newHeight}px`
  }, [])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!inputValue.trim() || isLoading || isRateLimited) return
      sendMessage(inputValue)
      setInputValue('')
      // Reset textarea height to minimum
      if (inputRef.current) {
        inputRef.current.style.height = `${MIN_HEIGHT}px`
      }
    },
    [inputValue, isLoading, isRateLimited, sendMessage]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit(e)
      }
    },
    [handleSubmit]
  )

  return (
    <div className="flex h-full flex-col">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto bg-background-secondary p-4">
        <div className="flex flex-col gap-4">
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center justify-between gap-2 border-t border-border bg-red-50 px-4 py-2">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
          <button
            onClick={retry}
            className="flex items-center gap-1 text-sm font-medium text-red-700 hover:text-red-800"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Rate limit warning */}
      {isRateLimited && (
        <div className="flex items-center gap-2 border-t border-border bg-amber-50 px-4 py-2 text-sm text-amber-700">
          <AlertCircle className="h-4 w-4" />
          {t('scoping.ai.rateLimitReached')}
        </div>
      )}

      {/* Input area */}
      <form onSubmit={handleSubmit} className="border-t border-border bg-white p-4">
        <div className="flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('scoping.ai.placeholder')}
            disabled={isLoading || isRateLimited}
            rows={3}
            style={{ height: MIN_HEIGHT }}
            className="flex-1 resize-none rounded-xl border border-border bg-background-secondary px-4 py-2.5 text-sm leading-relaxed text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading || isRateLimited}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
        {/* Message counter */}
        <div className="mt-2 text-right text-xs text-foreground-muted">
          {requestCount}/{maxRequests} {t('scoping.ai.messages')}
        </div>
      </form>
    </div>
  )
}
